import { buildDomainSeoBundle } from "./agenticSeoUtils";

export const AUTOMOTIVE_DOMAIN_INTRO = {
  title: "Automotive",
  content: "Agentic AI workflow automation for Automotive — across EV Manufacturing, After-Sales, and Engineering. Use cases span EV battery traceability and risk alerts, warranty claim triage with root cause analytics, and engineering change impact assessment. Practical deployments for OEMs, suppliers, and EV programs in India, UAE, KSA, UK, and USA with human-in-the-loop review, status tracking, and audit evidence.",
  seoDescription: "Automotive agentic AI use cases — EV battery traceability, warranty claim triage root cause analytics, engineering change impact assessment. MES QMS PLM ERP DMS integration. Agentic AI India UAE KSA UK USA.",
};

const slug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function withMeta(uc) { return { ...uc, groupId: slug(uc.subIndustry), seoTitle: uc.title, title: uc.shortTitle || uc.title }; }

const RAW = [
  {
    id: "ev-battery-traceability-and-risk-alerts",
    subIndustry: "EV Manufacturing",
    department: "Production / Quality",
    businessProcess: "EV battery traceability and risk alerts",
    regions: "India, UAE, UK, USA",
    shortTitle: "EV battery traceability and risk alerts",
    title: "Agentic AI for EV Battery Traceability",
    seoKeywords: "EV battery traceability, battery quality AI, automotive agentic AI, EV manufacturing automation",
    challenge: "Battery cell, module, and pack data is often spread across supplier files, test machines, MES, and spreadsheets. Risky lots may be difficult to trace quickly.",
    solution: "Create an Agentic AI traceability solution that connects cell test data, module assembly records, lot history, and quality exceptions to identify suspect packs and affected VINs.",
    summary: "EV Manufacturing teams can improve ev battery traceability and risk alerts with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "Agentic AI + Data Automation + Workflow Automation",
    buyerPersona: "COO, Plant Head, EV Program Head, Quality Head",
    systems: "MES, QMS, PLM, ERP, battery test systems, data lake",
    agentsRequired: "Traceability Agent, Battery Quality Agent, Exception Routing Agent",
    inputData: "Cell test results, lot data, BMS test data, supplier COA, assembly records, VIN mapping",
    outputActions: "Suspect lot alert, affected VIN list, quality hold recommendation, traceability report",
    keyBenefits: "Improves Battery Quality Control, Speeds Investigation, Supports Recall Readiness, And Improves Compliance Evidence.",
    roiTimeline: "6-12 months",
    implementationComplexity: "High",
    businessImpact: "High",
  },
  {
    id: "warranty-claim-triage-and-root-cause-analytics",
    subIndustry: "OEM / Dealer Network",
    department: "Warranty / After-Sales",
    businessProcess: "Warranty claim triage and root cause analytics",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Warranty claim triage and root cause analytics",
    title: "Agentic AI for Automotive Warranty Claims",
    seoKeywords: "automotive warranty automation, warranty claim AI, dealer claims automation, root cause analytics",
    challenge: "Warranty teams manually read dealer claims, job cards, photos, diagnostic reports, and customer complaints before approving or rejecting claims.",
    solution: "Agentic AI summarizes warranty claims, validates required evidence, clusters recurring failures, and recommends claim routing for human approval.",
    summary: "OEM / Dealer Network teams can improve warranty claim triage and root cause analytics with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "Agentic AI + Document AI + Data Automation",
    buyerPersona: "After-Sales Head, Warranty Head, Quality Head",
    systems: "Warranty System, Dealer Management System, CRM, ERP, QMS",
    agentsRequired: "Warranty Triage Agent, Evidence Check Agent, Failure Pattern Agent",
    inputData: "Warranty claim forms, job cards, diagnostic codes, photos, service history, parts data",
    outputActions: "Claim summary, missing evidence alert, risk flag, failure mode cluster, approval queue",
    keyBenefits: "Reduces Claim Review Effort, Improves Consistency, Identifies Recurring Defects Faster, And Supports Quality Feedback Loops.",
    roiTimeline: "6-12 months",
    implementationComplexity: "High",
    businessImpact: "High",
  },
  {
    id: "engineering-change-impact-assessment",
    subIndustry: "Engineering / Supply Chain",
    department: "Product Engineering",
    businessProcess: "Engineering change impact assessment",
    regions: "India, UK, USA",
    shortTitle: "Engineering change impact assessment",
    title: "Agentic AI for Automotive Engineering Change Management",
    seoKeywords: "engineering change automation, ECN AI agent, automotive PLM automation, BOM impact analysis AI",
    challenge: "Engineering change requests require manual review of affected BOMs, suppliers, inventory, work instructions, service parts, and open orders.",
    solution: "Agentic AI reviews change request documents, maps impacted parts and processes, and creates a cross-functional impact checklist for approval.",
    summary: "Engineering / Supply Chain teams can improve engineering change impact assessment with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "Agentic AI + Document AI + Workflow Automation",
    buyerPersona: "Engineering Head, CIO, Program Manager",
    systems: "PLM, ERP, MES, Supplier Portal, SharePoint",
    agentsRequired: "ECN Review Agent, BOM Impact Agent, Approval Routing Agent",
    inputData: "ECR/ECN documents, BOM, drawings, supplier list, inventory, work instructions, open orders",
    outputActions: "Impact checklist, affected part list, supplier alert, approval workflow",
    keyBenefits: "Reduces Engineering Change Delays, Improves Cross-Functional Visibility, And Reduces Missed Implementation Impacts.",
    roiTimeline: "6-12 months",
    implementationComplexity: "High",
    businessImpact: "High",
  },
];
export const AUTOMOTIVE_USE_CASES = RAW.map(withMeta);
const GROUP_ORDER = ["EV Manufacturing", "OEM / Dealer Network", "Engineering / Supply Chain"];
export const AUTOMOTIVE_GROUPS = GROUP_ORDER.map((label) => ({ id: slug(label), label })).filter((g) => AUTOMOTIVE_USE_CASES.some((uc) => uc.groupId === g.id));
const seo = buildDomainSeoBundle({ domainSlug: 'automotive-domain', domainIntro: AUTOMOTIVE_DOMAIN_INTRO, useCases: AUTOMOTIVE_USE_CASES });
export const AUTOMOTIVE_USE_CASES_SEO = seo.useCasesSeo;
export const AUTOMOTIVE_DOMAIN_FAQS = seo.domainFaqs;
export const AUTOMOTIVE_ITEM_LIST_JSONLD = seo.itemListJsonLd;
export const AUTOMOTIVE_FAQ_JSONLD = seo.faqJsonLd;
export const AUTOMOTIVE_BREADCRUMB_JSONLD = seo.breadcrumbJsonLd;
export const AUTOMOTIVE_WEBPAGE_JSONLD = seo.webPageJsonLd;

